import { useSyncExternalStore } from 'react';
import type { StoreApi, UseBoundStore } from 'zustand';
import { platformFallbackStore } from './platformFallbackStore';

export type GameStoreHook = UseBoundStore<StoreApi<any>>;

let activeStore: GameStoreHook | null = null;
const activeStoreSwitchListeners = new Set<() => void>();

function notifyActiveStoreSwitch(): void {
  activeStoreSwitchListeners.forEach(listener => listener());
}

function subscribeToActiveGameStore(onChange: () => void): () => void {
  let unsubscribeData = getActiveGameStore().subscribe(onChange);

  const handleActiveStoreSwitch = () => {
    unsubscribeData();
    unsubscribeData = getActiveGameStore().subscribe(onChange);
    onChange();
  };

  activeStoreSwitchListeners.add(handleActiveStoreSwitch);

  return () => {
    activeStoreSwitchListeners.delete(handleActiveStoreSwitch);
    unsubscribeData();
  };
}

export function registerActiveGameStore(store: GameStoreHook): void {
  activeStore = store;
  notifyActiveStoreSwitch();
}

export function unregisterActiveGameStore(): void {
  activeStore = null;
  platformFallbackStore.getState().setConnectionStatus('idle');
  notifyActiveStoreSwitch();
}

export function getActiveGameStoreOrNull(): GameStoreHook | null {
  return activeStore;
}

/** Active game store, or platform fallback when lobby/auth UI runs before mount. */
export function getActiveGameStore(): GameStoreHook {
  return activeStore ?? platformFallbackStore;
}

export function resolveGameStoreHook(controller?: {
  getGameStateStore?: () => GameStoreHook;
}): GameStoreHook {
  return controller?.getGameStateStore?.() ?? getActiveGameStore();
}

export function useActiveGameStore<T>(selector: (state: any) => T): T {
  return useSyncExternalStore(
    subscribeToActiveGameStore,
    () => selector(getActiveGameStore().getState()),
    () => selector(getActiveGameStore().getState())
  );
}

export function syncActiveGameBalance(balance: number, currency: string): void {
  const state = getActiveGameStore().getState() as {
    setBalance?: (b: number) => void;
    setCurrency?: (c: string) => void;
  };
  state.setBalance?.(balance);
  state.setCurrency?.(currency);
}
